import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AdminAuthProvider, useAdmin } from "@/lib/adminAuth";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import MagicLink from "@/pages/MagicLink";
import TenantLogin from "@/pages/TenantLogin";
import AgencyLayout from "@/pages/agency/AgencyLayout";
import Dashboard from "@/pages/agency/Dashboard";
import Clients from "@/pages/agency/Clients";
import Contracts from "@/pages/agency/Contracts";
import ContractDetail from "@/pages/agency/ContractDetail";
import BrandHub from "@/pages/agency/BrandHub";
import Roadmap from "@/pages/agency/Roadmap";
import Referrals from "@/pages/agency/Referrals";
import Leads from "@/pages/agency/Leads";
import PublicPage from "@/pages/agency/PublicPage";
import Invoices from "@/pages/agency/Invoices";
import Subscription from "@/pages/agency/Subscription";
import Feedback from "@/pages/agency/Feedback";
import Settings from "@/pages/agency/Settings";
import ClientPortal from "@/pages/portal/ClientPortal";
import ReferralLanding from "@/pages/ReferralLanding";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminAgencies from "@/pages/admin/AdminAgencies";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import "@/App.css";

function ProtectedAgency({ children }) {
  const { agency, loading } = useAuth();
  if (loading) return <div className="p-12 text-slate-400">Loading…</div>;
  if (!agency) return <Navigate to="/login" replace />;
  return children;
}

function ProtectedAdmin({ children }) {
  const { admin, loading } = useAdmin();
  if (loading) return <div className="p-12 text-slate-400">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/magic" element={<MagicLink />} />
            <Route path="/a/:subdomain" element={<TenantLogin />} />
            <Route path="/r/:code" element={<ReferralLanding />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedAdmin><AdminLayout /></ProtectedAdmin>}>
              <Route index element={<AdminOverview />} />
              <Route path="agencies" element={<AdminAgencies />} />
              <Route path="feedback" element={<AdminFeedback />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="payments" element={<AdminPayments />} />
            </Route>

            <Route path="/agency" element={<ProtectedAgency><AgencyLayout /></ProtectedAgency>}>
              <Route index element={<Dashboard />} />
              <Route path="clients" element={<Clients />} />
              <Route path="contracts" element={<Contracts />} />
              <Route path="contracts/:id" element={<ContractDetail />} />
              <Route path="brand-hub" element={<BrandHub />} />
              <Route path="roadmap" element={<Roadmap />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="leads" element={<Leads />} />
              <Route path="public-page" element={<PublicPage />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="feedback" element={<Feedback />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="/portal/*" element={<ClientPortal />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AdminAuthProvider>
    </AuthProvider>
  );
}
